import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { COMPANY } from '../data/mock';
import { Mail, MapPin, Clock, Phone, Globe, Send, Linkedin } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.42V13.2a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-.81-.07 4.83 4.83 0 01-.38-4.56z" />
  </svg>
);

export const ContactSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', message: '', subject: 'general'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Mock submission
    setTimeout(() => {
      toast.success('Message sent successfully! We will get back to you soon.', {
        description: 'Thank you for contacting SolarNova.',
      });
      setFormData({ name: '', email: '', company: '', message: '', subject: 'general' });
      setSubmitting(false);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <section id="contact" ref={ref} className="relative py-28 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="text-[#D4952B] text-sm font-semibold tracking-[0.2em] uppercase">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0D1B2A] mt-3 leading-tight">
            Contact Us
          </h2>
          <p className="text-2xl md:text-3xl text-[#0D1B2A]/30 font-semibold mt-2 font-['Noto_Sans_Arabic']">اتصل بنا</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-white rounded-2xl p-8 border border-[#0D1B2A]/5 shadow-sm">
              <h3 className="text-xl font-bold text-[#0D1B2A] mb-6">{COMPANY.fullName}</h3>
              
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#D4952B]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4952B]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0D1B2A]">Location</div>
                    <p className="text-[#1C2B33]/60 text-sm mt-0.5">{COMPANY.location}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#D4952B]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4952B]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0D1B2A]">Email</div>
                    <a href={`mailto:${COMPANY.email}`} className="text-[#0B3D5C] hover:text-[#D4952B] text-sm mt-0.5 transition-colors">{COMPANY.email}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#D4952B]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#D4952B]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0D1B2A]">Office Hours</div>
                    <p className="text-[#1C2B33]/60 text-sm mt-0.5">{COMPANY.hours}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#D4952B]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4952B]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0D1B2A]">Phone</div>
                    <a href={`tel:${COMPANY.phone}`} className="text-[#0B3D5C] hover:text-[#D4952B] text-sm mt-0.5 transition-colors">{COMPANY.phone}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#D4952B]/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-[#D4952B]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0D1B2A]">Website</div>
                    <a href={`https://${COMPANY.website}`} target="_blank" rel="noopener noreferrer" className="text-[#0B3D5C] hover:text-[#D4952B] text-sm mt-0.5 transition-colors">{COMPANY.website}</a>
                  </div>
                </div>
              </div>
            </div>

            {/* National Address & Social */}
            <div className="bg-[#0D1B2A] rounded-2xl p-8">
              <MapPin className="w-7 h-7 text-[#E8B84A] mb-4" />
              <p className="text-white/90 text-sm font-semibold mb-3">National Address</p>
              <div className="space-y-1.5 text-white/50 text-xs">
                <p>Building: {COMPANY.nationalAddress.building}</p>
                <p>Street: {COMPANY.nationalAddress.street}</p>
                <p>District: {COMPANY.nationalAddress.district}</p>
                <p>City: {COMPANY.nationalAddress.city}, {COMPANY.nationalAddress.postalCode}</p>
                <p>{COMPANY.nationalAddress.country}</p>
              </div>
              <div className="mt-4 pt-4 border-t border-white/8">
                <p className="text-white/90 text-sm font-semibold mb-3">Follow Us</p>
                <div className="flex items-center gap-2">
                  <a href={COMPANY.social.twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/50 hover:text-[#E8B84A] transition-all duration-300" aria-label="X">
                    <XIcon className="w-4 h-4" />
                  </a>
                  <a href={COMPANY.social.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/50 hover:text-[#E8B84A] transition-all duration-300" aria-label="LinkedIn">
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <a href={COMPANY.social.tiktok} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/50 hover:text-[#E8B84A] transition-all duration-300" aria-label="TikTok">
                    <TikTokIcon className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-[#0D1B2A]/5 shadow-sm">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-[#0D1B2A] mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#0D1B2A]/10 bg-[#FAFAF7] text-[#0D1B2A] text-sm placeholder:text-[#0D1B2A]/30 focus:outline-none focus:ring-2 focus:ring-[#D4952B]/30 focus:border-[#D4952B]/40 transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0D1B2A] mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#0D1B2A]/10 bg-[#FAFAF7] text-[#0D1B2A] text-sm placeholder:text-[#0D1B2A]/30 focus:outline-none focus:ring-2 focus:ring-[#D4952B]/30 focus:border-[#D4952B]/40 transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-[#0D1B2A] mb-2">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#0D1B2A]/10 bg-[#FAFAF7] text-[#0D1B2A] text-sm placeholder:text-[#0D1B2A]/30 focus:outline-none focus:ring-2 focus:ring-[#D4952B]/30 focus:border-[#D4952B]/40 transition-all"
                  placeholder="Your company name"
                />
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-[#0D1B2A] mb-2">Subject</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#0D1B2A]/10 bg-[#FAFAF7] text-[#0D1B2A] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4952B]/30 focus:border-[#D4952B]/40 transition-all"
                >
                  <option value="general">General Inquiry</option>
                  <option value="ess">Lithium-Ion Battery & ESS</option>
                  <option value="streetlights">Solar Streetlights</option>
                  <option value="transparent">Transparent Solar Panels</option>
                  <option value="pv">Custom PV Modules</option>
                  <option value="hybrid">Hybrid Systems</option>
                  <option value="bipv">Building-Integrated PV</option>
                  <option value="partnership">Partnership Opportunities</option>
                </select>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-[#0D1B2A] mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-[#0D1B2A]/10 bg-[#FAFAF7] text-[#0D1B2A] text-sm placeholder:text-[#0D1B2A]/30 focus:outline-none focus:ring-2 focus:ring-[#D4952B]/30 focus:border-[#D4952B]/40 transition-all resize-none"
                  placeholder="Tell us about your project requirements..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#D4952B] hover:bg-[#C08525] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-amber-900/20 hover:shadow-amber-800/40 text-sm tracking-wide"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
